import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { format } from "../lib/globalize-util";
import { $, merge } from "glamor";
import { IStore } from "reducers";
import type { IScope } from "reducers/scope";
import siteConfig from "../site-config";
import _ from "lodash";

const CHECK_TIMEOUT = 2 * 60 * 1000; // 2 min

type IStateProps = Pick<IScope, "isChildFrame">;

const styles = merge(
    {
        position: "fixed" as "fixed",
        display: "flex",
        top: "0",
        zIndex: 2900,
        left: "50%",
        transform: "translate(-50%, 0px)",
        backgroundColor: "rgb(45, 45, 45)",
    },
    $(" .wrapper", {
        padding: "0 16px",
        height: "48px",
        lineHeight: "48px",
        fontSize: "14px",
        color: "rgb(255, 255, 255)",
        minWidth: "288px",
        whiteSpace: "nowrap",
    }),
    $(" button", {
        border: "none",
        margin: "4px",
        padding: "0",
        outline: "none",
        height: "40px",
        lineHeight: "40px",
        minWidth: "88px",
        color: "rgb(47, 199, 190)",
        backgroundColor: "transparent",
        textAlign: "center" as "center",
    }),
);

const CheckForUpdates: React.FC<IStateProps> = ({ isChildFrame }) => {
    const [hasUpdates, setHasUpdates] = useState(false);
    const timer = React.useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(
        () => {
            if (siteConfig.pipelineIdUrl) {
                launchTimer();
            }
            return () => killTimer();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(
        () => {
            if (isChildFrame) {
                killTimer();
            } else {
                launchTimer();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isChildFrame],
    );

    const launchTimer = () => {
        killTimer();
        timer.current = setTimeout(checkUpdatesRecurring, CHECK_TIMEOUT);
    };

    const killTimer = () => {
        clearTimeout(timer.current);
    };

    const fetchCurrentPipelineId = async () => {
        const res = await fetch(siteConfig.pipelineIdUrl);
        if (!res.ok) {
            throw new Error("Network Error");
        }
        const pipelineId = await res.text();
        return (pipelineId || "").toString().trim();
    };

    const checkUpdatesRecurring = async () => {
        if (!_.isNil(siteConfig.pipelineId)) {
            try {
                const remotePipelineId = await fetchCurrentPipelineId();
                if (remotePipelineId === siteConfig.pipelineId) {
                    launchTimer();
                } else {
                    setHasUpdates(true);
                }
            } catch {
                launchTimer();
            }
        } else {
            setHasUpdates(false);
        }
    };

    const handleReload = () => {
        // @ts-ignore -- Firefox supports a non-standard forceGet boolean parameter for location.reload()
        window.location.reload(true);
    };

    if (!hasUpdates || isChildFrame) {
        return null;
    }

    return (
        <div {...styles}>
            <div className="wrapper">
                <span>{format("New version of NuORDER available")}</span>
                <button onClick={handleReload}>{format("Reload")}</button>
            </div>
        </div>
    );
};

const mapStateToProps = (state: IStore): IStateProps => ({
    isChildFrame: state.scope.isChildFrame,
});

export default connect(mapStateToProps)(CheckForUpdates);
